import axios from 'axios';
import { config } from '../config.js';

class ScreepsDashboard {
  constructor() {
    this.loading = false;
    this.statsData = [];
    this.charts = {};
    this.dataRange = null;
    this.startDate = null;
    this.endDate = null;
    this.navigationInitialized = false;
    this.currentPosition = 0;

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.loadInitialData();
  }

  setupEventListeners() {
    const periodSelect = document.getElementById('period-select');
    if (periodSelect) {
      periodSelect.addEventListener(
        'change',
        this.handlePeriodChange.bind(this),
      );
    }
  }

  async loadInitialData() {
    try {
      this.showFullPageLoading(true);

      // Load data range first
      await this.loadDataRange();

      await this.loadStats();

      // Get the selected period from the dropdown
      const periodSelect = document.getElementById('period-select');
      const selectedPeriod = periodSelect ? periodSelect.value : '1w';

      await this.loadAggregatedStats(selectedPeriod);
      this.renderDashboard();
    } catch (error) {
      this.showError('Failed to load statistics data');
    } finally {
      this.showFullPageLoading(false);
    }
  }

  async loadStats() {
    if (this.loading) return;
    this.loading = true;

    try {
      // Calculate dynamic limit based on date range
      let limit = 50; // Default for small ranges

      if (this.startDate && this.endDate && this.dataRange) {
        const startDate = new Date(this.startDate);
        const endDate = new Date(this.endDate);
        const earliestDate = new Date(this.dataRange.earliestDate);
        const latestDate = new Date(this.dataRange.latestDate);

        // Calculate what percentage of the total range is selected
        const totalRange = latestDate - earliestDate;
        const selectedRange = endDate - startDate;
        const rangePercentage = selectedRange / totalRange;

        // If user selects a large portion of the range, show more records
        if (rangePercentage > 0.5) {
          limit = Math.min(1000, this.dataRange.totalCount); // Show up to 1000 records for large ranges
        } else if (rangePercentage > 0.2) {
          limit = Math.min(500, this.dataRange.totalCount); // Show up to 500 records for medium ranges
        }
      }

      const params = new URLSearchParams({
        limit: limit.toString(),
      });

      // Use date range from slider if available
      if (this.startDate && this.endDate) {
        params.append('startDate', this.startDate);
        params.append('endDate', this.endDate);
      } else {
        // Fallback to default date range
        params.append('startDate', this.getStartDate());
        params.append('endDate', new Date().toISOString());
      }

      const response = await axios.get(
        `${config.apiUrl}/api/v1/screeps/stats?${params}`,
        {
          withCredentials: config.withCredentials,
        },
      );

      if (response.data.status === 'success') {
        const { stats } = response.data.data;
        // Reverse the order so most recent cards are on the right
        this.statsData = stats.reverse();
        this.updateDateRangeInfo();
      }
    } catch (error) {
      this.showError('Failed to load statistics');
    } finally {
      this.loading = false;
    }
  }

  async loadAggregatedStats(period, aggregation = 'hour') {
    try {
      const params = new URLSearchParams({
        period,
        aggregation,
      });

      const response = await axios.get(
        `${config.apiUrl}/api/v1/screeps/stats/aggregated?${params}`,
        {
          withCredentials: config.withCredentials,
        },
      );

      if (response.data.status === 'success') {
        this.renderCharts(response.data.data.aggregatedStats);
      }
    } catch (error) {
      this.showError('Failed to load aggregated statistics');
    }
  }

  async refreshData() {
    this.statsData = [];
    await this.loadInitialData();
  }

  async handlePeriodChange(event) {
    const period = event.target.value;

    try {
      this.showChartsLoading(true);
      await this.loadAggregatedStats(period);
    } catch (error) {
      this.showError('Failed to refresh charts for selected period');
    } finally {
      this.showChartsLoading(false);
    }
  }

  renderDashboard() {
    this.renderStatsList();
    this.renderSummaryCards();
  }

  renderStatsList() {
    const container = document.getElementById('stats-list-container');
    if (!container) {
      return;
    }

    const html = this.statsData.map(stat => this.createStatCard(stat)).join('');
    container.innerHTML = html;

    // Setup horizontal scrolling navigation only if not already initialized
    if (!this.navigationInitialized) {
      this.setupStatsNavigation();
    } else {
      // If navigation is already initialized, just apply the current position
      this.applyCurrentPosition();
    }
  }

  applyCurrentPosition() {
    if (!this.statsNavigation) return;

    // Always set position to rightmost cards when we have data
    if (this.statsData.length > 0) {
      this.currentPosition = this.statsData.length - 1;
    }

    this.statsNavigation.scrollToPosition(this.currentPosition);
  }

  setupStatsNavigation() {
    // Prevent re-initialization if already set up
    if (this.navigationInitialized) {
      return;
    }

    const container = document.getElementById('stats-list-container');
    const leftArrow = document.getElementById('stats-left-arrow');
    const rightArrow = document.getElementById('stats-right-arrow');
    const scrollbar = document.getElementById('stats-scrollbar');
    const scrollbarThumb = document.getElementById('stats-scrollbar-thumb');

    if (
      !container ||
      !leftArrow ||
      !rightArrow ||
      !scrollbar ||
      !scrollbarThumb
    )
      return;

    const cardWidth = 320; // 30rem + 2rem gap
    let isDragging = false;
    let dragStartX = 0;
    let dragStartLeft = 0;

    const updateArrows = () => {
      const currentMaxPosition = Math.max(0, this.statsData.length - 1);
      leftArrow.disabled = this.currentPosition >= currentMaxPosition; // Disable when at newest cards (rightmost)
      rightArrow.disabled = this.currentPosition === 0; // Disable when at oldest cards (leftmost)
    };

    const updateScrollbar = () => {
      const currentMaxPosition = Math.max(0, this.statsData.length - 1);
      if (currentMaxPosition === 0) {
        scrollbarThumb.style.width = '100%';
        scrollbarThumb.style.left = '0';
      } else {
        const thumbWidth = Math.max(
          20,
          scrollbar.offsetWidth / (currentMaxPosition + 1),
        );
        const thumbLeft =
          (this.currentPosition / currentMaxPosition) *
          (scrollbar.offsetWidth - thumbWidth);
        scrollbarThumb.style.width = `${thumbWidth}px`;
        scrollbarThumb.style.left = `${thumbLeft}px`;
      }
    };

    const scrollToPosition = position => {
      const currentMaxPosition = Math.max(0, this.statsData.length - 1);
      this.currentPosition = Math.max(
        0,
        Math.min(position, currentMaxPosition),
      );
      const translateX = -this.currentPosition * cardWidth;
      container.style.transform = `translateX(${translateX}px)`;
      updateArrows();
      updateScrollbar();
    };

    const getPositionFromThumbLeft = thumbLeft => {
      const scrollbarWidth = scrollbar.offsetWidth;
      const thumbWidth = scrollbarThumb.offsetWidth;
      const maxThumbLeft = scrollbarWidth - thumbWidth;
      const ratio = Math.max(0, Math.min(1, thumbLeft / maxThumbLeft));
      const currentMaxPosition = Math.max(0, this.statsData.length - 1);
      return Math.round(ratio * currentMaxPosition);
    };

    leftArrow.addEventListener('click', () => {
      const currentMaxPosition = Math.max(0, this.statsData.length - 1);
      if (this.currentPosition < currentMaxPosition) {
        scrollToPosition(this.currentPosition + 1);
      }
    });

    rightArrow.addEventListener('click', () => {
      if (this.currentPosition > 0) {
        scrollToPosition(this.currentPosition - 1);
      }
    });

    // Scroll bar click handler
    scrollbar.addEventListener('click', e => {
      if (isDragging) return; // Don't handle click if we're dragging

      const rect = scrollbar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const scrollbarWidth = scrollbar.offsetWidth;
      const currentMaxPosition = Math.max(0, this.statsData.length - 1);

      // Calculate position based on click location
      const clickRatio = clickX / scrollbarWidth;
      const newPosition = Math.round(clickRatio * currentMaxPosition);

      scrollToPosition(newPosition);
    });

    // Drag functionality for scroll bar thumb
    scrollbarThumb.addEventListener('mousedown', e => {
      isDragging = true;
      dragStartX = e.clientX;
      dragStartLeft = parseFloat(scrollbarThumb.style.left) || 0;

      // Add dragging styles
      scrollbarThumb.style.cursor = 'grabbing';
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';

      e.preventDefault();
    });

    document.addEventListener('mousemove', e => {
      if (!isDragging) return;

      const deltaX = e.clientX - dragStartX;
      const newThumbLeft = Math.max(
        0,
        Math.min(
          scrollbar.offsetWidth - scrollbarThumb.offsetWidth,
          dragStartLeft + deltaX,
        ),
      );

      const newPosition = getPositionFromThumbLeft(newThumbLeft);
      scrollToPosition(newPosition);
    });

    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        scrollbarThumb.style.cursor = 'grab';
        document.body.style.cursor = 'default';
        document.body.style.userSelect = 'auto';
      }
    });

    // Store navigation functions for external use
    this.statsNavigation = {
      scrollToPosition,
      updateArrows,
      updateScrollbar,
    };

    // Set initial position to rightmost cards if we have data
    if (this.statsData.length > 0) {
      this.currentPosition = this.statsData.length - 1;
      scrollToPosition(this.currentPosition);
    }

    // Set navigationInitialized flag
    this.navigationInitialized = true;
  }

  scrollToStatsPosition(position) {
    if (this.statsNavigation) {
      this.statsNavigation.scrollToPosition(position);
    }
  }

  createStatCard(stat) {
    const date = new Date(stat.collectedAt).toLocaleString();
    const cpuPercentage = ((stat.cpuUsed / stat.cpuLimit) * 100).toFixed(1);
    const colonyCount = stat.colonies ? stat.colonies.length : 0;
    const totalRooms = stat.colonies
      ? stat.colonies.reduce((sum, colony) => sum + (colony.roomCount || 0), 0)
      : 0;
    const totalSources = stat.colonies
      ? stat.colonies.reduce(
          (sum, colony) => sum + (colony.totalSources || 0),
          0,
        )
      : 0;
    const creepCount = stat.creepCount || 0;
    const flagCount = stat.flagCount || 0;

    // Get process counts
    const processes = stat.processes || {};
    const processCounts = processes.counts || {};
    const totalProcesses = processCounts.total || 0;
    const runningProcesses = processCounts.run || 0;
    const suspendedProcesses = processCounts.suspend || 0;
    const missedProcesses = processCounts.missed || 0;

    return `
      <div class="stat-card">
        <div class="stat-card__header">
          <h3 class="stat-card__title">${date}</h3>
          <span class="stat-card__cpu ${cpuPercentage > 80 ? 'high' : cpuPercentage > 50 ? 'medium' : 'low'}">
            CPU: ${cpuPercentage}%
          </span>
        </div>
        <div class="stat-card__content">
          <div class="stat-card__row">
            <span>Memory:</span>
            <span>${(stat.memorySize / 1024 / 1024).toFixed(2)} MB</span>
          </div>
          <div class="stat-card__row">
            <span>Colonies/Rooms/Sources:</span>
            <span>${colonyCount} / ${totalRooms} / ${totalSources}</span>
          </div>
          <div class="stat-card__row">
            <span>Creeps & Flags:</span>
            <span>${creepCount} / ${flagCount}</span>
          </div>
          <div class="stat-card__row">
            <span>Processes:</span>
            <span>${totalProcesses}</span>
          </div>
          <div class="stat-card__row">
            <span>Running:</span>
            <span>${runningProcesses}</span>
          </div>
          <div class="stat-card__row">
            <span>Suspended:</span>
            <span>${suspendedProcesses}</span>
          </div>
          <div class="stat-card__row">
            <span>Missed:</span>
            <span>${missedProcesses}</span>
          </div>
        </div>
      </div>
    `;
  }

  renderSummaryCards() {
    if (this.statsData.length === 0) {
      return;
    }

    const latest = this.statsData[this.statsData.length - 1];
    const container = document.getElementById('summary-cards');
    if (!container) {
      return;
    }

    const cpuPercentage = ((latest.cpuUsed / latest.cpuLimit) * 100).toFixed(1);
    const memoryUsed = (latest.memorySize / 1024 / 1024).toFixed(2);
    const totalRooms = latest.colonies
      ? latest.colonies.reduce(
          (sum, colony) => sum + (colony.roomCount || 0),
          0,
        )
      : 0;
    const totalSources = latest.colonies
      ? latest.colonies.reduce(
          (sum, colony) => sum + (colony.totalSources || 0),
          0,
        )
      : 0;

    // Calculate GCL progress percentage using the correct formula
    const currentProgress = latest.progress || 0;
    const progressTotal = latest.progressTotal || 0;

    const gclProgressPercentage =
      progressTotal > 0
        ? ((currentProgress / progressTotal) * 100).toFixed(1)
        : '0.0';

    // New metrics for second row
    const credits = latest.market?.credits
      ? (latest.market.credits / 1000000).toFixed(1)
      : '0.0';
    const pixels = latest.market?.availablePixels || 0;
    const creepCount = latest.creepCount || 0;
    const flagCount = latest.flagCount || 0;
    const pglLevel = latest.power?.level || 0;
    const powerProgress = latest.power?.progress || 0;
    const powerProgressTotal = latest.power?.progressTotal || 1;
    const powerProgressPercentage =
      powerProgressTotal > 0
        ? ((powerProgress / powerProgressTotal) * 100).toFixed(1)
        : '0.0';

    container.innerHTML = `
      <div class="summary-card">
        <h3>Current CPU Usage</h3>
        <div class="summary-card__value ${cpuPercentage > 80 ? 'high' : cpuPercentage > 50 ? 'medium' : 'low'}">
          ${cpuPercentage}%
        </div>
      </div>
      <div class="summary-card">
        <h3>Memory Used</h3>
        <div class="summary-card__value">${memoryUsed} MB</div>
      </div>
      <div class="summary-card">
        <h3>Rooms & Sources</h3>
        <div class="summary-card__value">${totalRooms} / ${totalSources}</div>
      </div>
      <div class="summary-card">
        <h3>GCL Level</h3>
        <div class="summary-card__value">${latest.gclLevel || 'N/A'}</div>
      </div>
      <div class="summary-card">
        <h3>GCL Progress</h3>
        <div class="summary-card__value">${gclProgressPercentage}%</div>
      </div>
      <div class="summary-card">
        <h3>Credits</h3>
        <div class="summary-card__value">${credits}M</div>
      </div>
      <div class="summary-card">
        <h3>Available Pixels</h3>
        <div class="summary-card__value">${pixels}</div>
      </div>
      <div class="summary-card">
        <h3>Creeps & Flags</h3>
        <div class="summary-card__value">${creepCount} / ${flagCount}</div>
      </div>
      <div class="summary-card">
        <h3>PGL Level</h3>
        <div class="summary-card__value">${pglLevel}</div>
      </div>
      <div class="summary-card">
        <h3>Power Progress</h3>
        <div class="summary-card__value">${powerProgressPercentage}%</div>
      </div>
    `;
  }

  renderCharts(aggregatedData) {
    // Render actual charts using Chart.js
    this.renderCpuChart(aggregatedData);
    this.renderColoniesChart(aggregatedData);
    this.renderEnergyChart(aggregatedData);
    this.renderMineralsChart(aggregatedData);
    this.renderProgressChart(aggregatedData);
    this.renderCreditsChart(aggregatedData);
  }

  renderCpuChart(data) {
    const canvas = document.getElementById('cpu-chart-canvas');
    if (!canvas) return;

    // Destroy existing chart if it exists
    if (this.charts.cpu) {
      this.charts.cpu.destroy();
    }

    // Prepare data for Chart.js
    const labels = data.map(d => {
      // Parse the _id string which is in format "YYYY-MM-DD-HH" or "YYYY-MM-DD"
      const parts = d._id.split('-');
      let date;
      if (parts.length === 4) {
        // Format: "YYYY-MM-DD-HH"
        date = new Date(parts[0], parts[1] - 1, parts[2], parts[3]);
      } else if (parts.length === 3) {
        // Format: "YYYY-MM-DD"
        date = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        // Fallback
        date = new Date(d._id);
      }

      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
      });
    });

    const cpuUsedData = data.map(d =>
      ((d.avgCpuUsed / d.avgCpuLimit) * 100).toFixed(1),
    );
    const cpuLimitData = data.map(d => 100); // Always 100% for limit line
    const totalProcessesData = data.map(d => d.avgTotalProcesses || 0);
    const runningProcessesData = data.map(d => d.avgRunningProcesses || 0);
    const suspendedProcessesData = data.map(d => d.avgSuspendedProcesses || 0);

    // Create the chart
    this.charts.cpu = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'CPU Usage',
            data: cpuUsedData,
            borderColor: '#244a92',
            backgroundColor: 'rgba(36, 74, 146, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            yAxisID: 'y',
            pointStyle: 'circle',
          },
          {
            label: 'CPU Limit',
            data: cpuLimitData,
            borderColor: '#eb4d4b',
            backgroundColor: 'rgba(235, 77, 75, 0.1)',
            borderWidth: 1,
            borderDash: [5, 5],
            fill: false,
            tension: 0,
            yAxisID: 'y',
            pointStyle: 'circle',
          },
          {
            label: 'Total Processes',
            data: totalProcessesData,
            borderColor: '#9b59b6',
            backgroundColor: 'rgba(155, 89, 182, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointStyle: 'triangle',
          },
          {
            label: 'Running Processes',
            data: runningProcessesData,
            borderColor: '#20bf6b',
            backgroundColor: 'rgba(32, 191, 107, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointStyle: 'triangle',
          },
          {
            label: 'Suspended Processes',
            data: suspendedProcessesData,
            borderColor: '#ff7730',
            backgroundColor: 'rgba(255, 119, 48, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointStyle: 'triangle',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          tooltip: {
            mode: 'index',
            intersect: false,
          },
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'CPU Usage (%)',
            },
            beginAtZero: true,
            max: 100,
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: 'Process Count',
            },
            grid: {
              drawOnChartArea: false,
            },
          },
          x: {
            title: {
              display: false,
            },
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    });
  }

  renderColoniesChart(data) {
    const canvas = document.getElementById('colonies-chart-canvas');
    if (!canvas) return;

    // Destroy existing chart if it exists
    if (this.charts.colonies) {
      this.charts.colonies.destroy();
    }

    // Prepare data for Chart.js
    const labels = data.map(d => {
      // Parse the _id string which is in format "YYYY-MM-DD-HH" or "YYYY-MM-DD"
      const parts = d._id.split('-');
      let date;
      if (parts.length === 4) {
        // Format: "YYYY-MM-DD-HH"
        date = new Date(parts[0], parts[1] - 1, parts[2], parts[3]);
      } else if (parts.length === 3) {
        // Format: "YYYY-MM-DD"
        date = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        // Fallback
        date = new Date(d._id);
      }

      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
      });
    });

    const coloniesData = data.map(d => d.totalColonies.toFixed(0));
    const roomsData = data.map(d => d.totalRooms.toFixed(1));
    const sourcesData = data.map(d => d.totalSources.toFixed(1));
    const creepCountData = data.map(d => d.avgCreepCount.toFixed(0));
    const flagCountData = data.map(d => d.avgFlagCount.toFixed(0));

    // Create the chart
    this.charts.colonies = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Colonies',
            data: coloniesData,
            borderColor: '#20bf6b',
            backgroundColor: 'rgba(32, 191, 107, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            yAxisID: 'y',
            pointStyle: 'circle',
          },
          {
            label: 'Total Rooms',
            data: roomsData,
            borderColor: '#ff7730',
            backgroundColor: 'rgba(255, 119, 48, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            yAxisID: 'y',
            pointStyle: 'circle',
          },
          {
            label: 'Total Sources',
            data: sourcesData,
            borderColor: '#9b59b6',
            backgroundColor: 'rgba(155, 89, 182, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
            pointStyle: 'circle',
          },
          {
            label: 'Creep Count',
            data: creepCountData,
            borderColor: '#e74c3c',
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointStyle: 'triangle',
          },
          {
            label: 'Flag Count',
            data: flagCountData,
            borderColor: '#f39c12',
            backgroundColor: 'rgba(243, 156, 18, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointStyle: 'triangle',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          tooltip: {
            mode: 'index',
            intersect: false,
          },
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Colonies, Rooms & Sources',
            },
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: 'Creeps & Flags',
            },
            grid: {
              drawOnChartArea: false,
            },
          },
          x: {
            title: {
              display: false,
            },
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    });
  }

  renderEnergyChart(data) {
    const canvas = document.getElementById('energy-chart-canvas');
    if (!canvas) return;

    // Destroy existing chart if it exists
    if (this.charts.energy) {
      this.charts.energy.destroy();
    }

    // Prepare data for Chart.js
    const labels = data.map(d => {
      // Parse the _id string which is in format "YYYY-MM-DD-HH" or "YYYY-MM-DD"
      const parts = d._id.split('-');
      let date;
      if (parts.length === 4) {
        // Format: "YYYY-MM-DD-HH"
        date = new Date(parts[0], parts[1] - 1, parts[2], parts[3]);
      } else if (parts.length === 3) {
        // Format: "YYYY-MM-DD"
        date = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        // Fallback
        date = new Date(d._id);
      }

      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
      });
    });

    const energyAvailableData = data.map(d =>
      (d.avgEnergyAvailable / 1000).toFixed(1),
    ); // Convert to thousands
    const energyCapacityData = data.map(d =>
      (d.avgEnergyCapacity / 1000).toFixed(1),
    ); // Convert to thousands
    const energyResourceData = data.map(d =>
      (d.avgEnergyResource / 1000).toFixed(1),
    ); // Convert to thousands

    // Create the chart
    this.charts.energy = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Energy in Spawns/Extensions',
            data: energyAvailableData,
            borderColor: '#f1c40f',
            backgroundColor: 'rgba(241, 196, 15, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            yAxisID: 'y',
            pointStyle: 'circle',
          },
          {
            label: 'Energy Capacity',
            data: energyCapacityData,
            borderColor: '#e67e22',
            backgroundColor: 'rgba(230, 126, 34, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
            pointStyle: 'circle',
          },
          {
            label: 'Energy Resource',
            data: energyResourceData,
            borderColor: '#27ae60',
            backgroundColor: 'rgba(39, 174, 96, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointStyle: 'triangle',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          tooltip: {
            mode: 'index',
            intersect: false,
          },
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Energy',
            },
            beginAtZero: true,
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: 'Energy Resource',
            },
            grid: {
              drawOnChartArea: false,
            },
          },
          x: {
            title: {
              display: false,
            },
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    });
  }

  renderMineralsChart(data) {
    const canvas = document.getElementById('minerals-chart-canvas');
    if (!canvas) return;

    // Destroy existing chart if it exists
    if (this.charts.minerals) {
      this.charts.minerals.destroy();
    }

    // Prepare data for Chart.js
    const labels = data.map(d => {
      // Parse the _id string which is in format "YYYY-MM-DD-HH" or "YYYY-MM-DD"
      const parts = d._id.split('-');
      let date;
      if (parts.length === 4) {
        // Format: "YYYY-MM-DD-HH"
        date = new Date(parts[0], parts[1] - 1, parts[2], parts[3]);
      } else if (parts.length === 3) {
        // Format: "YYYY-MM-DD"
        date = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        // Fallback
        date = new Date(d._id);
      }

      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
      });
    });

    const mineralHData = data.map(d => (d.avgMineralH / 1000).toFixed(1)); // Convert to thousands
    const mineralOData = data.map(d => (d.avgMineralO / 1000).toFixed(1)); // Convert to thousands
    const mineralUData = data.map(d => (d.avgMineralU / 1000).toFixed(1)); // Convert to thousands
    const mineralKData = data.map(d => (d.avgMineralK / 1000).toFixed(1)); // Convert to thousands
    const mineralLData = data.map(d => (d.avgMineralL / 1000).toFixed(1)); // Convert to thousands
    const mineralZData = data.map(d => (d.avgMineralZ / 1000).toFixed(1)); // Convert to thousands
    const mineralXData = data.map(d => (d.avgMineralX / 1000).toFixed(1)); // Convert to thousands
    const batteryData = data.map(d => (d.avgBattery / 1000).toFixed(1)); // Convert to thousands

    // Create the chart
    this.charts.minerals = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Hydrogen (H)',
            data: mineralHData,
            borderColor: '#e74c3c',
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
          },
          {
            label: 'Oxygen (O)',
            data: mineralOData,
            borderColor: '#3498db',
            backgroundColor: 'rgba(52, 152, 219, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
          },
          {
            label: 'Uranium (U)',
            data: mineralUData,
            borderColor: '#2ecc71',
            backgroundColor: 'rgba(46, 204, 113, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
          },
          {
            label: 'Keanium',
            data: mineralKData,
            borderColor: '#f39c12',
            backgroundColor: 'rgba(243, 156, 18, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
          },
          {
            label: 'Lemergium (L)',
            data: mineralLData,
            borderColor: '#9b59b6',
            backgroundColor: 'rgba(155, 89, 182, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
          },
          {
            label: 'Zynthium (Z)',
            data: mineralZData,
            borderColor: '#1abc9c',
            backgroundColor: 'rgba(26, 188, 156, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
          },
          {
            label: 'Catalyst (X)',
            data: mineralXData,
            borderColor: '#34495e',
            backgroundColor: 'rgba(52, 73, 94, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
          },
          {
            label: 'Battery',
            data: batteryData,
            borderColor: '#3498db',
            backgroundColor: 'rgba(52, 152, 219, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          tooltip: {
            mode: 'index',
            intersect: false,
          },
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Minerals',
            },
            beginAtZero: true,
          },
          x: {
            title: {
              display: false,
            },
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    });
  }

  renderProgressChart(data) {
    const canvas = document.getElementById('progress-chart-canvas');
    if (!canvas) return;

    // Destroy existing chart if it exists
    if (this.charts.progress) {
      this.charts.progress.destroy();
    }

    // Prepare data for Chart.js
    const labels = data.map(d => {
      // Parse the _id string which is in format "YYYY-MM-DD-HH" or "YYYY-MM-DD"
      const parts = d._id.split('-');
      let date;
      if (parts.length === 4) {
        // Format: "YYYY-MM-DD-HH"
        date = new Date(parts[0], parts[1] - 1, parts[2], parts[3]);
      } else if (parts.length === 3) {
        // Format: "YYYY-MM-DD"
        date = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        // Fallback
        date = new Date(d._id);
      }

      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
      });
    });

    // Calculate GCL progress percentage
    const gclProgressData = data.map(d => {
      const progress = d.avgGclProgress || 0;
      const total = d.avgGclProgressTotal || 1;
      return total > 0 ? ((progress / total) * 100).toFixed(1) : 0;
    });

    // Calculate GPL progress percentage
    const gplProgressData = data.map(d => {
      const progress = d.avgGplProgress || 0;
      const total = d.avgGplProgressTotal || 1;
      return total > 0 ? ((progress / total) * 100).toFixed(1) : 0;
    });

    // GCL and GPL progress totals
    const gclProgressTotalData = data.map(d => d.avgGclProgressTotal || 0);
    const gplProgressTotalData = data.map(d => d.avgGplProgressTotal || 0);

    // Create the chart
    this.charts.progress = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'GCL Progress (%)',
            data: gclProgressData,
            borderColor: '#3498db',
            backgroundColor: 'rgba(52, 152, 219, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            yAxisID: 'y',
            pointStyle: 'circle',
          },
          {
            label: 'GPL Progress (%)',
            data: gplProgressData,
            borderColor: '#e74c3c',
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            yAxisID: 'y',
            pointStyle: 'circle',
          },
          {
            label: 'GCL Progress Total',
            data: gclProgressTotalData,
            borderColor: '#2ecc71',
            backgroundColor: 'rgba(46, 204, 113, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointStyle: 'triangle',
          },
          {
            label: 'GPL Progress Total',
            data: gplProgressTotalData,
            borderColor: '#f39c12',
            backgroundColor: 'rgba(243, 156, 18, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1',
            pointStyle: 'triangle',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          tooltip: {
            mode: 'index',
            intersect: false,
          },
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Progress (%)',
            },
            beginAtZero: true,
            max: 100,
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            title: {
              display: true,
              text: 'Progress Total',
            },
            grid: {
              drawOnChartArea: false,
            },
          },
          x: {
            title: {
              display: false,
            },
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    });
  }

  renderCreditsChart(data) {
    const canvas = document.getElementById('credits-chart-canvas');
    if (!canvas) return;

    // Destroy existing chart if it exists
    if (this.charts.credits) {
      this.charts.credits.destroy();
    }

    // Prepare data for Chart.js
    const labels = data.map(d => {
      // Parse the _id string which is in format "YYYY-MM-DD-HH" or "YYYY-MM-DD"
      const parts = d._id.split('-');
      let date;
      if (parts.length === 4) {
        // Format: "YYYY-MM-DD-HH"
        date = new Date(parts[0], parts[1] - 1, parts[2], parts[3]);
      } else if (parts.length === 3) {
        // Format: "YYYY-MM-DD"
        date = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        // Fallback
        date = new Date(d._id);
      }

      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
      });
    });

    // Convert credits to millions for better display
    const creditsData = data.map(d => {
      const credits = d.avgCredits || 0;
      return (credits / 1000000).toFixed(2); // Convert to millions
    });

    // Create the chart
    this.charts.credits = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Credits (Millions)',
            data: creditsData,
            borderColor: '#9b59b6',
            backgroundColor: 'rgba(155, 89, 182, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointStyle: 'circle',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function (context) {
                const value = parseFloat(context.parsed.y);
                return `Credits: ${value.toFixed(2)}M`;
              },
            },
          },
        },
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Credits (Millions)',
            },
            beginAtZero: true,
          },
          x: {
            title: {
              display: false,
            },
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    });
  }

  getStartDate() {
    // Default to 1 week ago
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString();
  }

  showError(message) {
    // You can integrate this with your existing alert system
    console.error(message);
    // Example: showAlert(message, 'error');
  }

  showFullPageLoading(show) {
    const overlay = document.getElementById('loading-overlay-full');
    if (overlay) {
      overlay.style.display = show ? 'flex' : 'none';
    }
  }

  showChartsLoading(show) {
    const overlay = document.getElementById('loading-overlay-charts');
    if (overlay) {
      overlay.style.display = show ? 'flex' : 'none';
    }
  }

  async loadDataRange() {
    try {
      const response = await axios.get(
        `${config.apiUrl}/api/v1/screeps/stats/range`,
        {
          withCredentials: config.withCredentials,
        },
      );

      if (response.data.status === 'success') {
        this.dataRange = response.data.data;
        this.setupDateRangeSlider();
      }
    } catch (error) {
      this.showError('Failed to load data range');
    }
  }

  setupDateRangeSlider() {
    const minSlider = document.getElementById('date-range-min');
    const maxSlider = document.getElementById('date-range-max');
    const fill = document.querySelector('.date-range__fill');
    const info = document.getElementById('date-range-info');

    if (!minSlider || !maxSlider || !fill || !info || !this.dataRange) return;

    const earliestDate = new Date(this.dataRange.earliestDate);
    const latestDate = new Date(this.dataRange.latestDate);
    const totalDays = (latestDate - earliestDate) / (1000 * 60 * 60 * 24);

    // Set initial values (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const startPercent = Math.max(
      0,
      ((sevenDaysAgo - earliestDate) / (latestDate - earliestDate)) * 100,
    );
    const endPercent = 100;

    minSlider.value = startPercent;
    maxSlider.value = endPercent;

    const updateFill = () => {
      const minVal = parseInt(minSlider.value);
      const maxVal = parseInt(maxSlider.value);

      // Ensure min doesn't exceed max and vice versa
      if (minVal >= maxVal) {
        if (minSlider === document.activeElement) {
          maxSlider.value = minVal + 1;
        } else {
          minSlider.value = maxVal - 1;
        }
        return updateFill();
      }

      fill.style.left = `${minVal}%`;
      fill.style.width = `${maxVal - minVal}%`;

      // Update date range
      const startDate = new Date(
        earliestDate.getTime() + (minVal / 100) * (latestDate - earliestDate),
      );
      const endDate = new Date(
        earliestDate.getTime() + (maxVal / 100) * (latestDate - earliestDate),
      );

      this.startDate = startDate.toISOString();
      this.endDate = endDate.toISOString();

      // Update info display
      this.updateDateRangeInfo();
    };

    const handleSliderChange = async () => {
      updateFill();

      // Reload stats with new date range
      this.statsData = [];
      await this.loadStats();
      this.renderStatsList();
    };

    minSlider.addEventListener('input', updateFill);
    maxSlider.addEventListener('input', updateFill);
    minSlider.addEventListener('change', handleSliderChange);
    maxSlider.addEventListener('change', handleSliderChange);

    // Initial update
    updateFill();
  }

  updateDateRangeInfo() {
    const info = document.getElementById('date-range-info');
    if (!info || !this.dataRange) return;

    const loadedCount = this.statsData.length;
    const totalCount = this.dataRange.totalCount;

    if (this.startDate && this.endDate) {
      const startDate = new Date(this.startDate);
      const endDate = new Date(this.endDate);

      // Check if we're showing all available records in the selected range
      const isShowingAll = loadedCount >= totalCount || loadedCount >= 1000;
      const recordInfo = isShowingAll
        ? `${loadedCount} records`
        : `${loadedCount} of ${totalCount} records shown`;

      info.textContent = `Date Range: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()} (${recordInfo})`;
    } else {
      info.textContent = `Loading... (${loadedCount} of ${totalCount} records shown)`;
    }
  }
}

// Initialize dashboard when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Check if we're on the screeps dashboard page
  const isScreepsDashboard =
    window.location.pathname.includes('/dashboard') &&
    window.location.pathname.includes('/services/');

  // Check if required elements exist
  const requiredElements = [
    'stats-list-container',
    'stats-left-arrow',
    'stats-right-arrow',
    'stats-scrollbar',
    'stats-scrollbar-thumb',
    'summary-cards',
    'cpu-chart-canvas',
    'colonies-chart-canvas',
    'energy-chart-canvas',
    'minerals-chart-canvas',
    'progress-chart-canvas',
    'credits-chart-canvas',
  ];

  const missingElements = requiredElements.filter(
    id => !document.getElementById(id),
  );

  if (isScreepsDashboard && missingElements.length === 0) {
    new ScreepsDashboard();
  }
});

export default ScreepsDashboard;
